import React, { FC, ReactNode, useRef, useState } from 'react';
import { DateRangePicker, DateRange } from 'materialui-daterange-picker';
import { Box, Button, Popover, makeStyles, Theme, ButtonProps } from '@material-ui/core';

interface CustomDateRangePickerProps {
  startDate: Date | null;
  endDate: Date | null;
  onDateChange: (_range: DateRange) => void;
  startLabel?: string;
  endLabel?: string;
  buttonLabel?: string;
  size?: 'small' | 'medium' | 'large';
  fullWidth?: boolean;
  minDate?: Date;
  maxDate?: Date;
  className?: string;
  datePickerProps?: Partial<React.ComponentProps<typeof DateRangePicker>>;
  buttonProps?: Omit<ButtonProps, 'onClick' | 'className' | 'aria-describedby'>;
  popoverActions?: ReactNode;
  buttonLabelSuffix?: string;
}

const useStyles = makeStyles<Theme, { size: string, fullWidth: boolean }>((theme) => ({
  container: {
    display: 'flex',
    alignItems: 'center',
    width: props => props.fullWidth ? '100%' : 'auto',
  },
  button: {
    padding: theme.spacing(1, 2),
    minWidth: props => {
      switch (props.size) {
        case 'small': return 140;
        case 'large': return 240;
        default: return 200;
      }
    },
    width: props => props.fullWidth ? '100%' : 'auto',
  },
  popover: {
    padding: theme.spacing(2),
  },
  // Lifts the actions above the picker library's invisible full-screen click-away
  // layer so they stay clickable; that layer still handles outside-click closing.
  popoverActions: {
    position: 'relative',
    zIndex: 1,
    marginBottom: theme.spacing(1),
  }
}));

// Dates in and out of this component are local-midnight carriers of calendar dates;
// the label must therefore format in local time (see toPickerDate/toPlainDate).
const formatDisplayDate = (date: Date | null): string =>
  date ? date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' }) : '';

const CustomDateRangePicker: FC<CustomDateRangePickerProps> = ({
  startDate,
  endDate,
  onDateChange,
  startLabel = 'Start Date',
  endLabel = 'End Date',
  buttonLabel = 'Select a Date Range',
  size = 'medium',
  fullWidth = false,
  minDate,
  maxDate,
  className,
  datePickerProps = {},
  buttonProps = {},
  popoverActions,
  buttonLabelSuffix,
  ...rest
}) => {
  const classes = useStyles({ size, fullWidth });
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const actionRef = useRef<any>(null);

  const handleClose = () => {
    setIsOpen(false);
  };

  const handleDateRangeChange = (range: DateRange) => {
    onDateChange(range);
    handleClose();
  };

  const getButtonText = () => {
    const suffix = buttonLabelSuffix ? ` ${buttonLabelSuffix}` : '';
    if (startDate && endDate) {
      return `${formatDisplayDate(startDate)} - ${formatDisplayDate(endDate)}${suffix}`;
    }
    if (startDate) {
      return `${startLabel}: ${formatDisplayDate(startDate)}${suffix}`;
    }
    if (endDate) {
      return `${endLabel}: ${formatDisplayDate(endDate)}${suffix}`;
    }
    return buttonLabel;
  };

  const initialDateRange: DateRange = {
    startDate: startDate || null,
    endDate: endDate || null,
  };

  return (
    <Box className={`${classes.container} ${className || ''}`}>
      <Button
        color="default"
        ref={actionRef}
        onClick={() => setIsOpen(true)}
        className={classes.button}
        {...buttonProps}
        {...rest}
      >
        {getButtonText()}
      </Button>

      <Popover
        anchorEl={actionRef.current}
        open={isOpen}
        onClose={handleClose}
        getContentAnchorEl={null}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <Box className={classes.popover}>
          {popoverActions && <Box className={classes.popoverActions}>{popoverActions}</Box>}
          <DateRangePicker
            open={isOpen}
            toggle={() => {
              if (isOpen) {
                handleClose();
              }
            }}
            onChange={handleDateRangeChange}
            initialDateRange={initialDateRange}
            minDate={minDate}
            maxDate={maxDate}
            {...datePickerProps}
          />
        </Box>
      </Popover>
    </Box>
  );
};

export default CustomDateRangePicker;
